import type { CurrentState } from './sources/currents';
import { nextTide, type TideEvent } from './sources/tides';
import { cardinal } from './types';

/**
 * Bay-wide context strip: tide and tidal current at the Golden Gate.
 *
 * This lives once in the sidebar rather than inside every station popup. The
 * popup version was showing Gate tides under stations like Redwood City, whose
 * tides run well over half an hour different — one honest reference beats a
 * dozen mislabelled ones.
 */
const timeFmt = new Intl.DateTimeFormat([], { hour: 'numeric', minute: '2-digit' });

export function renderGateStrip(
  host: HTMLElement,
  tides: TideEvent[],
  current: CurrentState | null,
  now: Date = new Date(),
): void {
  const tide = nextTide(tides, now);

  if (!tide && !current) {
    host.hidden = true;
    return;
  }
  host.hidden = false;

  const rows: string[] = [];

  if (current) {
    const stream =
      current.phase === 'slack'
        ? 'Slack water'
        : `${current.phase === 'ebb' ? 'Ebb' : 'Flood'} ` +
          `${current.trend === 'building' ? 'building to' : 'easing from'} ` +
          `${current.peakKt.toFixed(1)} kt`;

    const setting =
      current.phase === 'slack'
        ? ''
        : ` <span class="gate-dim">sets ${cardinal(current.setDeg)}</span>`;

    rows.push(`<span class="gate-main">${stream}</span>${setting}`);

    if (current.nextSlack) {
      rows.push(`<span class="gate-dim">Slack ${timeFmt.format(current.nextSlack)}</span>`);
    }
  }

  if (tide) {
    rows.push(
      `<span class="gate-dim">${tide.kind === 'high' ? 'High' : 'Low'} ` +
        `${timeFmt.format(tide.t)} · ${tide.heightFt.toFixed(1)} ft</span>`,
    );
  }

  host.innerHTML = `
    <p class="gate-title">Golden Gate <span class="gate-tag">predicted</span></p>
    <p class="gate-rows">${rows.join('<span class="gate-sep">·</span>')}</p>`;
}
